import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react'
import EntryStatsRow from '../components/EntryStatsRow'
import Layout from '../components/Layout'
import LeaderboardTable from '../components/LeaderboardTable'
import ShareButton from '../components/ShareButton'
import { renderLeaderboardShareCard, SHARE_CARD_VARIANTS } from '../lib/shareCard'
import { getShareUrl } from '../lib/shareUrl'
import { Avatar } from '../components/ui/avatar'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet'
import { useNotification } from '../contexts/NotificationContext'
import { useUser } from '../contexts/UserContext'
import { track } from '../lib/analytics'
import { cn } from '../lib/utils'
import { compareDivisions } from '../lib/divisionOrder'
import {
  fetchActiveSports,
  fetchDivisionsBySport,
  fetchLeaderboardCount,
  fetchLeaderboardRankings,
  fetchTournamentsBySport,
  fetchUserGroups,
  fetchUserLeaderboardPage,
} from '../services/leaderboardService'

const PAGE_SIZE = 50

// Derive initial filter state from URL search params so Leaderboards is shareable.
function readFiltersFromUrl(searchParams, currentYear) {
  const yearParam = searchParams.get('year')
  return {
    group: searchParams.get('group') || 'global',
    year: yearParam && yearParam !== 'all' ? yearParam : currentYear.toString(),
    sport: searchParams.get('sport') || 'all',
    tournament: searchParams.get('tournament') || 'all',
    division: searchParams.get('division') || 'all',
  }
}

export default function Leaderboards() {
  const { user } = useUser()
  const notify = useNotification()
  const [searchParams, setSearchParams] = useSearchParams()

  const currentYear = useMemo(() => new Date().getFullYear(), [])
  const yearOptions = useMemo(() => {
    const out = []
    for (let year = currentYear; year >= 2025; year--) out.push(year)
    return out
  }, [currentYear])

  // All filters live in one object — one source of truth, one URL-sync effect, one fetch effect.
  const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams, currentYear))
  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }))

  const [leaderboard, setLeaderboard] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [sports, setSports] = useState([])
  const [tournaments, setTournaments] = useState([])
  const [divisions, setDivisions] = useState([])
  const [userGroups, setUserGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [userRank, setUserRank] = useState(null)
  // DUB-200: "any points in this filter scope" must be sticky across pages —
  // rows sort by points desc, so page 2+ can be all zero-point rows even when
  // scoring has started. Latched by the fetch effect, reset on filter change
  // (which also resets page to 0, so the latch re-primes from page 0).
  const [seenPoints, setSeenPoints] = useState(false)
  const [jumpVisible, setJumpVisible] = useState(false)
  const [jumpDirection, setJumpDirection] = useState('down')
  const [scrolled, setScrolled] = useState(false)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [pendingScrollToMe, setPendingScrollToMe] = useState(false)
  const tableWrapperRef = useRef(null)

  // ── Effect 1: load static reference data once. ──────────────────────────
  // DUB-74: when the platform has only one active sport (e.g. admin disabled
  // everything except Pickleball), the leaderboard's Sport filter should
  // default to that sport rather than "All sports" — the "All" option is
  // meaningless when there's nothing to differentiate. We snapshot the URL's
  // initial ?sport= once so deep links that explicitly chose "all" still
  // honor that choice; only first-load-without-an-explicit-sport overrides.
  const initialSportParamRef = useRef(searchParams.get('sport'))
  useEffect(() => {
    let cancelled = false
    Promise.all([fetchActiveSports().catch((err) => { console.error(err); return [] })])
      .then(([sportsData]) => {
        if (cancelled) return
        const list = sportsData || []
        setSports(list)
        if (list.length === 1 && !initialSportParamRef.current) {
          setFilters((prev) => prev.sport === 'all' ? { ...prev, sport: list[0].id } : prev)
        }
      })
    return () => { cancelled = true }
  }, [])

  // ── Effect 2: load current user's groups when user id appears. ──────────
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    fetchUserGroups(user.id)
      .then((data) => { if (!cancelled) setUserGroups(data || []) })
      .catch((err) => {
        console.error('Error loading user groups:', err)
        if (!cancelled) {
          notify.error('Failed to load groups')
          setUserGroups([])
        }
      })
    return () => { cancelled = true }
  }, [user?.id, notify])

  // ── Effect 3: load tournaments + divisions when sport changes. ──────────
  // We only reset the tournament/division filters when the sport actually
  // changes from one value to another — not on first mount, so deep links
  // like /leaderboards?sport=X&tournament=Y keep their scope. StrictMode
  // double-invokes effects in dev, so we key off a ref that starts equal to
  // the initial sport; "changed" only becomes true after a real user change.
  const prevSportRef = useRef(filters.sport)
  useEffect(() => {
    const sportChanged = prevSportRef.current !== filters.sport
    prevSportRef.current = filters.sport

    if (filters.sport === 'all') {
      setTournaments([])
      setDivisions([])
      if (sportChanged) {
        setFilters((prev) => prev.tournament === 'all' && prev.division === 'all'
          ? prev
          : { ...prev, tournament: 'all', division: 'all' })
      }
      return
    }
    let cancelled = false
    Promise.all([
      fetchTournamentsBySport(filters.sport).catch((err) => { console.error(err); return [] }),
      fetchDivisionsBySport(filters.sport).catch((err) => { console.error(err); return [] }),
    ]).then(([t, d]) => {
      if (cancelled) return
      setTournaments(t || [])
      setDivisions(d || [])
    })
    if (sportChanged) {
      setFilters((prev) => ({ ...prev, tournament: 'all', division: 'all' }))
    }
    return () => { cancelled = true }
  }, [filters.sport])

  // ── Effect 4: fetch leaderboard + count whenever filters/page change. ───
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const args = {
      p_group_id: filters.group === 'global' ? null : filters.group,
      p_sport_id: filters.sport === 'all' ? null : filters.sport,
      p_tournament_id: filters.tournament === 'all' ? null : filters.tournament,
      p_division_id: filters.division === 'all' ? null : filters.division,
      p_year: filters.year === 'all' ? null : parseInt(filters.year, 10),
    }
    track('leaderboard_viewed', {
      sport: filters.sport,
      group: filters.group,
      tournament: filters.tournament,
      year: filters.year,
    })
    Promise.all([
      fetchLeaderboardRankings({ ...args, p_limit: PAGE_SIZE, p_offset: page * PAGE_SIZE }),
      fetchLeaderboardCount(args),
    ])
      .then(([rows, count]) => {
        if (cancelled) return
        setLeaderboard(rows || [])
        setTotalCount(count || 0)
        if ((rows || []).some((e) => (e.total_points || 0) > 0)) setSeenPoints(true)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error loading leaderboard:', err)
        notify.error('Failed to load leaderboard')
        setLeaderboard([])
        setTotalCount(0)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [filters, page, notify])

  // ── Effect 5: sync filters into URL (replace, not push). ────────────────
  useEffect(() => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v) })
    setSearchParams(params, { replace: true })
  }, [filters, setSearchParams])

  // Reset to first page (and the points latch) whenever filters change.
  useEffect(() => {
    setPage(0)
    setSeenPoints(false)
  }, [filters])

  // ── Effect 6: look up the current user's rank/page so the "Jump to me" FAB
  // knows whether the user is in this filtered leaderboard at all, and which
  // page their row lives on. Does NOT auto-paginate or auto-scroll — we stay
  // at the top of the page; the FAB is the only way to jump.
  useEffect(() => {
    if (!user?.id) { setUserRank(null); return }
    let cancelled = false
    fetchUserLeaderboardPage({
      p_group_id: filters.group === 'global' ? null : filters.group,
      p_sport_id: filters.sport === 'all' ? null : filters.sport,
      p_tournament_id: filters.tournament === 'all' ? null : filters.tournament,
      p_division_id: filters.division === 'all' ? null : filters.division,
      p_year: filters.year === 'all' ? null : parseInt(filters.year, 10),
      p_page_size: PAGE_SIZE,
    })
      .then((out) => { if (!cancelled) setUserRank(out?.rank ?? null) })
      .catch((err) => {
        console.error('Error fetching user page:', err)
        if (!cancelled) setUserRank(null)
      })
    return () => { cancelled = true }
  }, [filters, user?.id])

  // ── Effect 7a: after the FAB paginates to the user's page, scroll their
  // row into view once the new rows render.
  useEffect(() => {
    if (!pendingScrollToMe || loading) return
    const el = tableWrapperRef.current?.querySelector('.leaderboard-you')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setPendingScrollToMe(false)
    }
  }, [pendingScrollToMe, leaderboard, loading])

  // ── Effect 7: show the "Jump to me" FAB when the user is in the leaderboard
  // but their row isn't currently visible — either on a different page (no
  // element) or scrolled off viewport.
  useEffect(() => {
    if (!userRank) { setJumpVisible(false); return }
    const el = tableWrapperRef.current?.querySelector('.leaderboard-you')
    if (!el) { setJumpVisible(true); setJumpDirection('down'); return }
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setJumpVisible(!entry.isIntersecting)
        if (!entry.isIntersecting) {
          const viewportCenter = window.innerHeight / 2
          setJumpDirection(entry.boundingClientRect.top < viewportCenter ? 'up' : 'down')
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [userRank, leaderboard, loading])

  // ── Effect 9: track whether the user has scrolled past the top — drives the
  // expanded-vs-compact rank card swap (and on mobile, the swap between the
  // expanded rank card and the slim sticky header). Hysteresis: enter sticky
  // mode at >140px, exit below 80px. Without the gap, the height delta
  // between the two layouts (~45px) caused scrollY to bounce across a single
  // threshold and the header would flicker between modes (DUB-issue).
  useEffect(() => {
    const onScroll = () => {
      setScrolled(prev => {
        if (!prev && window.scrollY > 140) return true
        if (prev && window.scrollY < 80) return false
        return prev
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleJumpToMe = async () => {
    if (!user?.id) return
    try {
      const out = await fetchUserLeaderboardPage({
        p_group_id: filters.group === 'global' ? null : filters.group,
        p_sport_id: filters.sport === 'all' ? null : filters.sport,
        p_tournament_id: filters.tournament === 'all' ? null : filters.tournament,
        p_division_id: filters.division === 'all' ? null : filters.division,
        p_year: filters.year === 'all' ? null : parseInt(filters.year, 10),
        p_page_size: PAGE_SIZE,
      })
      if (out?.page_index != null && out.page_index !== page) {
        setPendingScrollToMe(true)
        setPage(out.page_index)
      } else {
        const el = tableWrapperRef.current?.querySelector('.leaderboard-you')
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    } catch (err) {
      console.error('Jump to me failed:', err)
      notify.error('Unable to find your row')
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const sortedDivisions = useMemo(
    () => [...divisions].sort(compareDivisions),
    [divisions],
  )

  const shareSubtitle = (() => {
    const groupName = filters.group === 'global'
      ? 'Global'
      : (userGroups.find(g => g.id === filters.group)?.name || 'Group')
    const sportName = filters.sport === 'all'
      ? 'All sports'
      : (sports.find(s => s.id === filters.sport)?.name || '')
    const playerCount = totalCount > 0 ? `${totalCount.toLocaleString()} participants` : null
    return [groupName, sportName, playerCount].filter(Boolean).join(' • ')
  })()

  const filterDefs = [
    {
      key: 'group',
      label: 'Group',
      options: [{ value: 'global', label: 'Global' }, ...userGroups.map(g => ({ value: g.id, label: g.name }))],
    },
    {
      key: 'sport',
      label: 'Sport',
      options: [{ value: 'all', label: 'All sports' }, ...sports.map(s => ({ value: s.id, label: s.name }))],
    },
    {
      key: 'year',
      label: 'Year',
      options: [{ value: 'all', label: 'All time' }, ...yearOptions.map(y => ({ value: y.toString(), label: y.toString() }))],
    },
    {
      key: 'tournament',
      label: 'Tournament',
      disabled: filters.sport === 'all',
      options: [{ value: 'all', label: 'All tournaments' }, ...tournaments.map(t => ({ value: t.id, label: t.name }))],
    },
    {
      key: 'division',
      label: 'Division',
      disabled: filters.sport === 'all',
      options: [{ value: 'all', label: 'All divisions' }, ...sortedDivisions.map(d => ({ value: d.id, label: d.name }))],
    },
  ]

  const activeFilterCount = [
    filters.group !== 'global',
    filters.sport !== 'all',
    filters.year !== currentYear.toString(),
    filters.tournament !== 'all',
    filters.division !== 'all',
  ].filter(Boolean).length

  const clearAllFilters = () => setFilters({
    group: 'global',
    sport: 'all',
    year: currentYear.toString(),
    tournament: 'all',
    division: 'all',
  })

  const shareButton = !loading && leaderboard.length > 0 ? (
    <ShareButton
      iconOnly
      ariaLabel="Share leaderboard"
      title="Dub leaderboard"
      text="Check out the leaderboard on dub."
      url={getShareUrl()}
      className="h-10 w-10 p-0 rounded-full bg-background shrink-0"
      variants={SHARE_CARD_VARIANTS}
      getImage={(variantKey) => renderLeaderboardShareCard({
        title: 'Who got the dub?',
        subtitle: shareSubtitle,
        variant: variantKey,
        rows: leaderboard.slice(0, 5).map(r => ({
          rank: r.rank,
          full_name: r.full_name,
          total_points: r.total_points,
          is_current_user: r.is_current_user,
          accuracy: r.avg_accuracy,
          potential_points: r.potential_points,
        })),
      })}
    />
  ) : null

  const userEntry = leaderboard.find(e => e.is_current_user) || null
  // DUB-123: when the user has submitted but no scores have been entered
  // anywhere in the slice yet, showing a degenerate "#1 of N" is misleading.
  // Falls back to true when the leaderboard hasn't loaded so we don't briefly
  // flash the "submitted" state. DUB-200: the seenPoints latch (not just the
  // visible slice) decides this, so a zero-point page 2 doesn't regress a
  // ranked user to "Picks submitted".
  const scoringHasStarted = leaderboard.length === 0
    ? true
    : seenPoints || leaderboard.some(e => (e.total_points || 0) > 0)
  // DUB-200: the user's row is only in `leaderboard` on the page it ranks on —
  // paging away must not flip the CTA back to "Submit a bracket". userRank
  // comes from get_user_leaderboard_page, which ranks every entry in scope
  // regardless of points, so it's the authoritative "has an entry" signal.
  const hasEntry = !!userEntry || userRank != null

  const rankCardProps = {
    userId: user?.id,
    userRank,
    totalCount,
    hasEntry,
    scoringHasStarted,
    jumpVisible,
    jumpDirection,
    onJumpToMe: handleJumpToMe,
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title row: title left, rank card + share centered in the right column
            (matches the sidebar + main column widths used below, so the rank card
            sits directly above the leaderboard table). */}
        <div className="mb-6 lg:flex lg:items-center lg:gap-8">
          <div className="lg:w-72 lg:shrink-0">
            <h1 className="text-3xl font-bold mb-2">Leaderboards</h1>
            <p className="text-muted-foreground">See how you stack up</p>
          </div>
          {!scrolled && user?.id && (
            <div className="hidden lg:block lg:flex-1 lg:min-w-0 mt-4 lg:mt-0">
              <div className="max-w-2xl mx-auto flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <ExpandedUserRankCard
                    userRank={userRank}
                    totalCount={totalCount}
                    userEntry={userEntry}
                    user={user}
                    hasEntry={hasEntry}
                    scoringHasStarted={scoringHasStarted}
                    jumpVisible={jumpVisible}
                    jumpDirection={jumpDirection}
                    onJumpToMe={handleJumpToMe}
                  />
                </div>
                {shareButton}
              </div>
            </div>
          )}
        </div>

        <div className="lg:flex lg:gap-8">
          {/* Desktop sidebar: rank + share + filters stacked in a sticky column */}
          <aside className="hidden lg:block lg:w-72 lg:shrink-0">
            <div className="sticky top-4 space-y-4">
              {scrolled && (
                <div className="flex items-center gap-2">
                  <UserRankSummary {...rankCardProps} className="flex-1 min-w-0" />
                  {shareButton}
                </div>
              )}
              <div className="space-y-2">
                {filterDefs.map(f => (
                  <FilterChip
                    key={f.key}
                    label={f.label}
                    value={filters[f.key]}
                    onChange={(v) => setFilter(f.key, v)}
                    items={f.options}
                    disabled={f.disabled}
                    fullWidth
                  />
                ))}
              </div>
            </div>
          </aside>

          {/* Main column */}
          <main className="flex-1 min-w-0">
            {/* Single filter sheet, controlled — its trigger lives in the rank card
                row when not scrolled, and in the sticky bar when scrolled. */}
            <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
              <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-3">
                  {filterDefs.map(f => (
                    <FilterChip
                      key={f.key}
                      label={f.label}
                      value={filters[f.key]}
                      onChange={(v) => setFilter(f.key, v)}
                      items={f.options}
                      disabled={f.disabled}
                      fullWidth
                    />
                  ))}
                  {activeFilterCount > 0 && (
                    <Button
                      variant="outline"
                      onClick={clearAllFilters}
                      className="w-full mt-2"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear all filters
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Mobile sticky header: compact rank + filter + share, only when scrolled */}
            {scrolled && (
              <div className="lg:hidden sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-3 bg-background/95 backdrop-blur-sm border-b border-border/60 mb-4">
                <div className="flex items-center justify-between gap-2">
                  {user?.id ? (
                    <UserRankSummary {...rankCardProps} />
                  ) : (
                    <div />
                  )}
                  <div className="flex items-center gap-2">
                    <FilterTriggerButton
                      activeFilterCount={activeFilterCount}
                      onClick={() => setFilterSheetOpen(true)}
                    />
                    {shareButton}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile rank card (when not scrolled): filter + share on top row,
                full-width rank pill below so it doesn't get squished. */}
            {!scrolled && (
              <div className="lg:hidden mb-4 space-y-2">
                <div className="flex items-center justify-end gap-2">
                  <FilterTriggerButton
                    activeFilterCount={activeFilterCount}
                    onClick={() => setFilterSheetOpen(true)}
                  />
                  {shareButton}
                </div>
                {user?.id && (
                  <ExpandedUserRankCard
                    userRank={userRank}
                    totalCount={totalCount}
                    userEntry={userEntry}
                    user={user}
                    hasEntry={hasEntry}
                    scoringHasStarted={scoringHasStarted}
                    jumpVisible={jumpVisible}
                    jumpDirection={jumpDirection}
                    onJumpToMe={handleJumpToMe}
                  />
                )}
              </div>
            )}

            <div ref={tableWrapperRef}>
              <LeaderboardTable
                leaderboard={leaderboard}
                loading={loading}
                scope={filters.tournament !== 'all' || filters.division !== 'all' ? 'tournament' : 'global'}
                singleBracket={filters.division !== 'all'}
                scoringHasStarted={scoringHasStarted}
                emptyMessage="No rankings available"
              />
            </div>

            {totalCount > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                  </Button>
                  <span className="text-muted-foreground">Page {page + 1} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </Layout>
  )
}

function ExpandedUserRankCard({ userRank, totalCount, userEntry, user, hasEntry: hasEntryProp, scoringHasStarted = true, jumpVisible, jumpDirection, onJumpToMe }) {
  const name = userEntry?.full_name || user?.full_name || user?.first_name || 'You'
  // DUB-100 / DUB-123: three-state CTA.
  //   - no entry          → "Submit a bracket"
  //   - entry, no scoring → "Picks submitted" + "Awaiting results"
  //   - entry + scoring   → "#N of M"
  // hasEntry is provided explicitly by the page (more reliable than userRank,
  // which can be null on slices where the user just isn't visible).
  const hasEntry = hasEntryProp ?? (userRank != null)
  const showRank = hasEntry && scoringHasStarted && userRank != null

  const baseClasses = "flex items-center gap-3 sm:gap-4 rounded-2xl px-4 sm:px-5 py-3 w-full lg:w-auto text-left transition-[filter] hover:brightness-95 active:brightness-90 cursor-pointer"

  const body = (
    <>
      <Avatar
        size="md"
        fullName={name}
        avatarUrl={userEntry?.avatar_url}
        avatarType={userEntry?.avatar_type}
      />
      <div className="flex flex-col shrink-0">
        <span
          className="text-[10px] font-bold uppercase tracking-wider leading-none"
          style={{ color: 'var(--color-royal-purple)' }}
        >
          Your rank
        </span>
        {showRank ? (
          <div className="flex items-baseline gap-1.5">
            <span
              className="text-2xl sm:text-3xl font-extrabold leading-none"
              style={{ color: 'var(--color-royal-purple)' }}
            >
              #{userRank}
            </span>
            <span className="text-xs sm:text-sm" style={{ color: 'var(--color-ink)', opacity: 0.7 }}>
              of {totalCount.toLocaleString()}
            </span>
          </div>
        ) : hasEntry ? (
          <div className="flex flex-col">
            <span className="text-sm font-semibold mt-0.5" style={{ color: 'var(--color-ink)' }}>
              Picks submitted
            </span>
            <span className="text-[11px]" style={{ color: 'var(--color-ink)', opacity: 0.65 }}>
              Awaiting results
            </span>
          </div>
        ) : (
          <span className="text-sm font-semibold mt-0.5" style={{ color: 'var(--color-ink)' }}>
            Submit a bracket
          </span>
        )}
      </div>
      {jumpVisible && (
        <span
          className="ml-auto flex items-center gap-1 text-sm font-semibold shrink-0"
          style={{ color: 'var(--color-royal-purple)' }}
        >
          <span className="hidden sm:inline">Jump to me</span>
          {jumpDirection === 'up' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
        </span>
      )}
    </>
  )

  return (
    <button
      type="button"
      onClick={onJumpToMe}
      aria-label={jumpVisible ? "Jump to my row" : "Scroll to my row"}
      className={baseClasses}
      style={{ backgroundColor: 'var(--color-lavender)' }}
    >
      {body}
    </button>
  )
}

function UserRankSummary({ userId, userRank, totalCount, hasEntry = userRank != null, scoringHasStarted = true, jumpVisible, jumpDirection, onJumpToMe, className }) {
  const base = cn("flex items-center gap-2 rounded-full px-3 sm:px-4 h-10 transition-colors", className)

  if (!userId) {
    return (
      <div className={cn(base, "bg-[var(--color-lavender)]/40 text-muted-foreground text-sm")}>
        Leaderboards
      </div>
    )
  }
  // DUB-123: three-state — no entry / entry-no-score / ranked. The compact
  // pill drops the "Awaiting results" subline since space is tight; the full
  // ExpandedUserRankCard shows it.
  if (!hasEntry) {
    return (
      <div className={cn(base, "bg-[var(--color-lavender)]/60 text-sm whitespace-nowrap")} style={{ color: 'var(--color-ink)' }}>
        Submit a bracket
      </div>
    )
  }
  if (!scoringHasStarted || userRank == null) {
    return (
      <div className={cn(base, "bg-[var(--color-lavender)]/60 text-sm whitespace-nowrap")} style={{ color: 'var(--color-ink)' }}>
        Picks submitted
      </div>
    )
  }

  const content = (
    <>
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-royal-purple)' }}>
        You
      </span>
      <span className="text-lg sm:text-xl font-extrabold leading-none" style={{ color: 'var(--color-royal-purple)' }}>
        #{userRank}
      </span>
      <span className="hidden sm:inline text-sm whitespace-nowrap" style={{ color: 'var(--color-ink)', opacity: 0.7 }}>
        of {totalCount.toLocaleString()}
      </span>
      {jumpVisible && (
        <span className="flex items-center" style={{ color: 'var(--color-royal-purple)' }}>
          {jumpDirection === 'up' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
        </span>
      )}
    </>
  )

  return (
    <button
      type="button"
      onClick={onJumpToMe}
      className={cn(base, "bg-[var(--color-lavender)] hover:brightness-95 active:brightness-90 cursor-pointer")}
      aria-label={jumpVisible ? "Jump to my row" : "Scroll to my row"}
    >
      {content}
    </button>
  )
}

function FilterTriggerButton({ activeFilterCount, onClick }) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      className="h-10 px-3 gap-1.5 rounded-full shrink-0"
      aria-label="Open filters"
    >
      <Filter className="h-4 w-4" />
      {activeFilterCount > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-primary-foreground">
          {activeFilterCount}
        </span>
      )}
    </Button>
  )
}

function FilterChip({ label, value, onChange, items, disabled = false, fullWidth = false }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={cn(
          "h-9 rounded-full px-4 text-sm gap-1.5 whitespace-nowrap border",
          // Full-width chips live in the desktop sidebar. Keep the label
          // anchored left but push the selected value + chevron together to
          // the right so the chip reads "Group: ........ Global ⌄" instead
          // of spacing all three parts evenly across the row. The trigger's
          // children are: <span>label</span>, <span>SelectValue</span>, <svg>
          // — nudging the second span with ml-auto carries the chevron along.
          fullWidth
            ? "w-full [&>span:nth-of-type(2)]:ml-auto"
            : "w-auto shrink-0"
        )}
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-lavender) 30%, white)',
          borderColor: 'color-mix(in srgb, var(--color-purple) 20%, transparent)',
        }}
      >
        <span className="text-muted-foreground font-normal">{label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((it) => (
          <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
